import React from "react";
import { useFormContext } from "react-hook-form";
import { MapPin } from "lucide-react";
import { FaSpinner } from "react-icons/fa";

const ADDRESS_FIELD_MAX_LENGTH = 60;
const ADDRESS_FIELD_PATTERN = /[^A-Za-z0-9,.\-/# ]/g;

const LocationSection = ({ primaryBookingId, locationOptions, isResolvingLocation }) => {
    const { register, setValue, watch, formState: { errors } } = useFormContext();
    const flatNo = watch("flatNo") || "";
    const buildingName = watch("buildingName") || "";
    const locality = watch("locality") || "";
    const landmark = watch("landmark") || "";
    const locationName = watch("locationName") || "";
    const pincode = watch("pincode") || "";

    const updateCombinedAddress = (flat, building, loc, land) => {
        setValue("addressLine", [flat, building, loc, land].map((s) => s.trim()).filter(Boolean).join(", "), { shouldValidate: true });
    };

    return (
        <div className="hvt-quick-section">
            <div className="hvt-quick-section-title"><MapPin size={13} /> Location</div>

            <div className="hvt-field-grid">
                <div className="hvt-field">
                    <label className="required">Pincode</label>
                    <div className="position-relative">
                        <input type="text" className="form-control" value={pincode} disabled readOnly />
                        <input type="hidden" {...register("pincode", { required: "Required" })} />
                        {isResolvingLocation && <FaSpinner className="fa-spin position-absolute" style={{ right: 10, top: 12 }} />}
                    </div>
                    {errors.pincode && <small className="text-danger">{errors.pincode.message}</small>}
                </div>

                <div className="hvt-field">
                    <label className="required">Location</label>
                    {primaryBookingId ? (
                        <>
                            <input type="text" className="form-control" value={locationName} disabled readOnly />
                            <input type="hidden" {...register("locationId", { required: "Required" })} />
                        </>
                    ) : (
                        <select
                            className="form-select"
                            disabled={!locationOptions.length}
                            {...register("locationId", {
                                required: "Required",
                                onChange: (e) => {
                                    const selected = locationOptions.find((l) => String(l.Locationid) === e.target.value) || null;
                                    setValue("locationName", selected?.Location || "");
                                },
                            })}
                        >
                            <option value="">
                                {isResolvingLocation ? "Resolving..." : locationOptions.length ? "Select Location" : "Enter pincode first"}
                            </option>
                            {locationOptions.map((loc) => (
                                <option key={loc.Locationid} value={loc.Locationid}>{loc.Location}</option>
                            ))}
                        </select>
                    )}
                    {errors.locationId && <small className="text-danger">{errors.locationId.message}</small>}
                </div>
            </div>

            <div className="hvt-field-grid mt-2">
                <div className="hvt-field">
                    <div className="d-flex justify-content-between align-items-center">
                        <label className="required mb-0">Flat / House No.</label>
                        <small className="text-muted">{flatNo.length}/{ADDRESS_FIELD_MAX_LENGTH}</small>
                    </div>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Flat 302"
                        maxLength={ADDRESS_FIELD_MAX_LENGTH}
                        disabled={!!primaryBookingId}
                        {...register("flatNo", {
                            required: "Required",
                            maxLength: { value: ADDRESS_FIELD_MAX_LENGTH, message: `Max ${ADDRESS_FIELD_MAX_LENGTH} characters` },
                            onChange: (e) => {
                                const val = e.target.value.replace(ADDRESS_FIELD_PATTERN, "").slice(0, ADDRESS_FIELD_MAX_LENGTH);
                                setValue("flatNo", val, { shouldValidate: true });
                                updateCombinedAddress(val, buildingName, locality, landmark);
                            },
                        })}
                    />
                    {errors.flatNo && <small className="text-danger">{errors.flatNo.message}</small>}
                </div>

                <div className="hvt-field">
                    <div className="d-flex justify-content-between align-items-center">
                        <label className="required mb-0">Building Name</label>
                        <small className="text-muted">{buildingName.length}/{ADDRESS_FIELD_MAX_LENGTH}</small>
                    </div>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Sunrise Apartments"
                        maxLength={ADDRESS_FIELD_MAX_LENGTH}
                        disabled={!!primaryBookingId}
                        {...register("buildingName", {
                            required: "Required",
                            maxLength: { value: ADDRESS_FIELD_MAX_LENGTH, message: `Max ${ADDRESS_FIELD_MAX_LENGTH} characters` },
                            onChange: (e) => {
                                const val = e.target.value.replace(ADDRESS_FIELD_PATTERN, "").slice(0, ADDRESS_FIELD_MAX_LENGTH);
                                setValue("buildingName", val, { shouldValidate: true });
                                updateCombinedAddress(flatNo, val, locality, landmark);
                            },
                        })}
                    />
                    {errors.buildingName && <small className="text-danger">{errors.buildingName.message}</small>}
                </div>
            </div>

            <div className="hvt-field-grid mt-2">
                <div className="hvt-field">
                    <div className="d-flex justify-content-between align-items-center">
                        <label className="required mb-0">Locality</label>
                        <small className="text-muted">{locality.length}/{ADDRESS_FIELD_MAX_LENGTH}</small>
                    </div>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. MG Road"
                        maxLength={ADDRESS_FIELD_MAX_LENGTH}
                        disabled={!!primaryBookingId}
                        {...register("locality", {
                            required: "Required",
                            maxLength: { value: ADDRESS_FIELD_MAX_LENGTH, message: `Max ${ADDRESS_FIELD_MAX_LENGTH} characters` },
                            onChange: (e) => {
                                const val = e.target.value.replace(ADDRESS_FIELD_PATTERN, "").slice(0, ADDRESS_FIELD_MAX_LENGTH);
                                setValue("locality", val, { shouldValidate: true });
                                updateCombinedAddress(flatNo, buildingName, val, landmark);
                            },
                        })}
                    />
                    {errors.locality && <small className="text-danger">{errors.locality.message}</small>}
                </div>

                <div className="hvt-field">
                    <div className="d-flex justify-content-between align-items-center">
                        <label className="required mb-0">Landmark</label>
                        <small className="text-muted">{landmark.length}/{ADDRESS_FIELD_MAX_LENGTH}</small>
                    </div>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. near City Hospital"
                        maxLength={ADDRESS_FIELD_MAX_LENGTH}
                        disabled={!!primaryBookingId}
                        {...register("landmark", {
                            required: "Required",
                            maxLength: { value: ADDRESS_FIELD_MAX_LENGTH, message: `Max ${ADDRESS_FIELD_MAX_LENGTH} characters` },
                            onChange: (e) => {
                                const val = e.target.value.replace(ADDRESS_FIELD_PATTERN, "").slice(0, ADDRESS_FIELD_MAX_LENGTH);
                                setValue("landmark", val, { shouldValidate: true });
                                updateCombinedAddress(flatNo, buildingName, locality, val);
                            },
                        })}
                    />
                    {errors.landmark && <small className="text-danger">{errors.landmark.message}</small>}
                </div>
            </div>

            <input type="hidden" {...register("addressLine", { required: "Required" })} />
        </div>
    );
};

export default LocationSection;
